import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TopBar } from '@/features/header/components/TopBar/TopBar';
import type { SearchResult } from '@/features/header/components/TopBar/TopBar';
import { FilterBar } from '@/features/header/components/FilterBar/FilterBar';
import { MetricsUsageBanner } from '@/core/components/MetricsUsageBanner/MetricsUsageBanner';
import { GlobalFilterModal } from '@/features/global-filter/components/GlobalFilterModal';
import { GLOBAL_FILTER_EVENTS } from '@/features/global-filter/constants/events';
import { prefetchSelectorItems } from '@/core/hooks/useSelectorItems';
import { useExcludedChannelKeys } from '@/features/global-filter/hooks/useExcludedChannelKeys';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';

import { useSidebarOpen } from '@/features/header/hooks/useSidebarOpen';
import './Header.scss';

interface HeaderProps {
    userData?: string | {
    userId: string;
    username: string;
    initials: string;
    avatar: string;
    };
    reprocessingStatus?: string;
    availableItems?: SearchResult[];
    filterLabels?: string[];
    hideTopBar?: boolean;
    hideFilterBar?: boolean;
    hideSearchBar?: boolean;
    hideLanguageSelector?: boolean;
    hideReprocessingStatus?: boolean;
    hideSidebarButton?: boolean;
    hideCopyFilterIdButton?: boolean;
    hideClearFilterButton?: boolean;
    showGlobalFilter?: boolean;
    lockRefreshInterval?: boolean;
    excludedChannelKeys?: string[];
}

const HEADER_HEIGHT_EVENT = 'mfe-header-height-change';
const HEADER_HEIGHT_CSS_VAR = '--mfe-header-height';
// The CSS custom property REPROCESSING_STATUS_RIGHT_CSS_VAR is part of the integration contract and is consumed by the host application.
const REPROCESSING_STATUS_RIGHT_CSS_VAR = '--mfe-reprocessing-status-right';
const HEADER_TOPBAR_HEIGHT_CSS_VAR = '--mfe-top-bar-height';
const FILTER_BAR_HEIGHT_CSS_VAR = '--mfe-filter-bar-height';
const DATA_COLLECTED_VISIBILITY_EVENT = 'mfe-menu-data-collected-visibility-change';
const MENU_HAS_DATA_COLLECTED_ATTR = 'data-mfe-menu-has-data-collected';
const PAGE_AUTHENTICATION_WARNING_EVENT = 'page-authentication-warning';
const PAGE_AUTHENTICATION_WARNING_LINK_CLICKED_EVENT = 'page-authentication-warning-link-clicked';
const PAGE_INTERACTIONS_CHANGE_WARNING_EVENT = 'interaction-types-notification';
const SCROLL_HIDE_START_Y = 120;
const SCROLL_DELTA_TO_HIDE = 16;
const SCROLL_DELTA_TO_SHOW = 8;
// Minimum reserved top spacing so page titles never collapse against the viewport edge
// when the header bars are scrolled out of view (e.g. hideTopBar hosts like warroom).
const MIN_HEADER_HEIGHT_PX = 24;


export const Header: React.FC<HeaderProps> = ({
  userData,
  reprocessingStatus,
  availableItems = [],
  filterLabels = [],
  hideTopBar = false,
  hideFilterBar = false,
  hideSearchBar = false,
  hideLanguageSelector = false,
  hideReprocessingStatus = false,
  hideSidebarButton = false,
  hideCopyFilterIdButton = false,
  hideClearFilterButton = false,
  showGlobalFilter = false,
  lockRefreshInterval = false,
  excludedChannelKeys: excludedChannelKeysProp,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const isSidebarOpen = useSidebarOpen();
  const [hasDataCollectedOnMenu, setHasDataCollectedOnMenu] = useState(
    document.documentElement.getAttribute(MENU_HAS_DATA_COLLECTED_ATTR) === 'true',
  );
  const [isGlobalFilterOpen, setIsGlobalFilterOpen] = useState(false);
  const excludedChannelKeys = useExcludedChannelKeys(excludedChannelKeysProp);
  const { showToast, showToastWithLink } = useToast();
  const lastScrollY = useRef(0);
  const topBarRef = useRef<HTMLDivElement>(null);
  const filterBarRef = useRef<HTMLDivElement>(null);
  const metricsUsageBannerRef = useRef<HTMLDivElement>(null);

  const emitHeight = useCallback(() => {
    const topBarHeight = hideTopBar ? 0 : (topBarRef.current?.offsetHeight ?? 0);
    const filterBarHeight = (!hideFilterBar && isVisible)
      ? (filterBarRef.current?.offsetHeight ?? 0)
      : 0;
    const metricsUsageBannerHeight = (hasDataCollectedOnMenu && isVisible)
      ? (metricsUsageBannerRef.current?.offsetHeight ?? 0)
      : 0;
    const totalHeight = topBarHeight + filterBarHeight + metricsUsageBannerHeight;
    // Clamp to a minimum so the value never goes negative (which would make the
    // whole `padding` shorthand invalid and drop ALL page padding, not just the top).
    const headerHeight = Math.max(totalHeight - 34, MIN_HEADER_HEIGHT_PX);

    // Only set CSS variables when we have real measurements to avoid initial layout jumps
    // during component mount. The fallback values in CSS will be used until the first real measurement.
    if (!hideTopBar && topBarHeight > 0) {
      document.documentElement.style.setProperty(HEADER_TOPBAR_HEIGHT_CSS_VAR, `${topBarHeight}px`);
    }
    if (!hideFilterBar && filterBarHeight >= 0) {
      document.documentElement.style.setProperty(FILTER_BAR_HEIGHT_CSS_VAR, `${filterBarHeight}px`);
    }
    // Always set header height since it has a minimum value
    document.documentElement.style.setProperty(HEADER_HEIGHT_CSS_VAR, `${headerHeight}px`);

    // Calculate and sync the status button right position, clearing stale values when it can't be measured.
    const shouldMeasureStatus = !hideTopBar && !hideReprocessingStatus;
    const statusButton = shouldMeasureStatus
      ? (topBarRef.current?.querySelector('[data-testid="reprocessing-status-button"]') as HTMLElement | null)
      : null;
    const container = statusButton?.offsetParent as HTMLElement | null;
    const rightPosition = statusButton && container
      ? `${container.clientWidth - (statusButton.offsetLeft + statusButton.offsetWidth)}px`
      : null;

    if (rightPosition) {
      document.documentElement.style.setProperty(REPROCESSING_STATUS_RIGHT_CSS_VAR, rightPosition);
    } else {
      document.documentElement.style.removeProperty(REPROCESSING_STATUS_RIGHT_CSS_VAR);
    }

    document.dispatchEvent(
      new CustomEvent(HEADER_HEIGHT_EVENT, {
        detail: { height: totalHeight, isTopBarHidden: hideTopBar },
        bubbles: true,
        composed: true,
      }),
    );
  }, [hideFilterBar, isVisible, hideTopBar, hideReprocessingStatus, hasDataCollectedOnMenu]);

  useEffect(() => {
    if (showGlobalFilter) prefetchSelectorItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const elements = [topBarRef.current, filterBarRef.current, metricsUsageBannerRef.current].filter(Boolean) as Element[];
    const observer = new ResizeObserver(emitHeight);
    elements.forEach((el) => observer.observe(el));
    emitHeight();
    return () => observer.disconnect();
  }, [emitHeight]);

  const controlHeader = useCallback(() => {
    const currentScrollY = window.scrollY;
    const delta = currentScrollY - lastScrollY.current;

    // Keeps header visible near the top and avoids toggling on tiny scroll changes.
    if (currentScrollY <= SCROLL_HIDE_START_Y) {
      setIsVisible(true);
      lastScrollY.current = currentScrollY;
      return;
    }

    if (delta > SCROLL_DELTA_TO_HIDE) {
      setIsVisible(false);
    } else if (delta < -SCROLL_DELTA_TO_SHOW) {
      setIsVisible(true);
    }

    lastScrollY.current = currentScrollY;
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', controlHeader);
    return () => {
      window.removeEventListener('scroll', controlHeader);
    };
  }, [controlHeader]);

  useEffect(() => {
    const handleDataCollectedVisibility = (event: Event) => {
      const customEvent = event as CustomEvent<{ visible?: boolean }>;
      setHasDataCollectedOnMenu(Boolean(customEvent.detail?.visible));
    };

    document.addEventListener(DATA_COLLECTED_VISIBILITY_EVENT, handleDataCollectedVisibility);

    return () => {
      document.removeEventListener(DATA_COLLECTED_VISIBILITY_EVENT, handleDataCollectedVisibility);
    };
  }, []);

  useEffect(() => {
    const handleAuthenticationWarning = (event: Event) => {
      const customEvent = event as CustomEvent<{ message?: string }>;
      const htmlMessage = customEvent.detail?.message;

      if (htmlMessage) {
        showToastWithLink({
          variant: 'warning',
          title: '',
          text: htmlMessage,
          icon: 'warning',
          duration: 10,
          linkStyles: {
            color: '#1e6bf1',
            textDecoration: 'underline',
            cursor: 'pointer'
          },
          onLinkClick: (event) => {
            event.preventDefault();
            document.dispatchEvent(new CustomEvent(PAGE_AUTHENTICATION_WARNING_LINK_CLICKED_EVENT));
          },
        });
      }
    };

    document.addEventListener(PAGE_AUTHENTICATION_WARNING_EVENT, handleAuthenticationWarning);

    return () => {
      document.removeEventListener(PAGE_AUTHENTICATION_WARNING_EVENT, handleAuthenticationWarning);
    };
  }, [showToastWithLink]);

    useEffect(() => {
    const handleInteractionsChangeWarning = (event: Event) => {
      const customEvent = event as CustomEvent<{ message?: string }>;
      const htmlMessage = customEvent.detail?.message;

      if (htmlMessage) {
        showToast({
          variant: 'system',
          title: '',
          text: htmlMessage,
          icon: 'info',
          duration: 15,
        });
      }
    };

    document.addEventListener(PAGE_INTERACTIONS_CHANGE_WARNING_EVENT, handleInteractionsChangeWarning);

    return () => {
      document.removeEventListener(PAGE_INTERACTIONS_CHANGE_WARNING_EVENT, handleInteractionsChangeWarning);
    };
  }, [showToast]);

  const actualIsSidebarOpen = hideTopBar ? false : isSidebarOpen;

  return (
    <>
      {!hideTopBar && (
        <div
          ref={topBarRef}
          className={`header-container header-container--top-bar ${isVisible ? 'header-container--visible' : 'header-container--hidden'}`}
        >
          <TopBar
            userData={userData}
            reprocessingStatus={reprocessingStatus}
            availableItems={availableItems}
            hideLanguageSelector={hideLanguageSelector}
            hideSearchBar={hideSearchBar}
            hideReprocessingStatus={hideReprocessingStatus}
            hideSidebarButton={hideSidebarButton}
          />
        </div>
      )}
      {!hideFilterBar && (
        <div
          ref={filterBarRef}
          className={`header-container header-container--filter-bar ${isVisible ? 'header-container--visible' : 'header-container--hidden'} ${hideTopBar ? 'header-container--no-top-bar' : ''}`}
        >
          <FilterBar
            filterLabels={filterLabels}
            hideTopBar={hideTopBar}
            hideCopyFilterIdButton={hideCopyFilterIdButton}
            hideClearFilterButton={hideClearFilterButton}
            lockRefreshInterval={lockRefreshInterval}
            onOpenFilter={showGlobalFilter ? () => setIsGlobalFilterOpen(true) : undefined}
          />
        </div>
      )}
      {hasDataCollectedOnMenu && (
        <div
          ref={metricsUsageBannerRef}
          className={`header-container header-container--metrics-usage-banner ${isVisible ? 'header-container--visible' : 'header-container--hidden'} ${hideTopBar ? 'header-container--no-top-bar' : ''}`}
        >
          <div
            className={`header-container__metrics-usage-banner-wrapper ${
              actualIsSidebarOpen
                ? 'header-container__metrics-usage-banner-wrapper--sidebar-open'
                : 'header-container__metrics-usage-banner-wrapper--full-width'
            }`}
          >
            <MetricsUsageBanner />
          </div>
        </div>
      )}
      {showGlobalFilter && isGlobalFilterOpen && (
        <GlobalFilterModal
          onClose={() => {
            setIsGlobalFilterOpen(false);
            globalThis.dispatchEvent(new CustomEvent(GLOBAL_FILTER_EVENTS.CLOSED));
          }}
          excludedChannelKeys={excludedChannelKeys}
        />
      )}
    </>
  );
};
